import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Type, Image as ImageIcon, Video, Lightbulb, HelpCircle,
  ChevronUp, ChevronDown, Trash2, Plus, Upload, Target, Bell, AlertTriangle, Info, Check, X, FolderOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import RichTextEditor from './RichTextEditor';
import MediaPicker from './MediaPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const uid = () => Math.random().toString(36).slice(2, 10);

export const HIGHLIGHT_VARIANTS = {
  objetivo: { label: 'Objetivos', icon: Target, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  recordatorio: { label: 'Recordatorio', icon: Bell, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  importante: { label: 'Importante', icon: AlertTriangle, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  nota: { label: 'Nota', icon: Info, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
};

const resolveImg = (url) => (url && url.startsWith('/api') ? `${BACKEND}${url}` : url);
const countBlanks = (text) => (String(text || '').match(/_{3,}/g) || []).length;

// Normaliza el valor del campo de vídeo: si pegan el código de inserción (<iframe>),
// extrae el src y decodifica entidades, para guardar una URL limpia (evita bloqueos del WAF).
const normalizeVideoInput = (val) => {
  let u = String(val || '');
  if (u.includes('<iframe')) {
    const m = u.match(/src=["']([^"']+)["']/i);
    if (m) u = m[1];
  }
  return u.replace(/&amp;/g, '&');
};

const HuecoAnswerInput = ({ value, onCommit }) => {
  const [raw, setRaw] = useState((value || []).join(', '));
  useEffect(() => { setRaw((value || []).join(', ')); }, [value]);
  return (
    <Input
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => onCommit(raw.split(',').map((s) => s.trim()).filter((s) => s !== ''))}
      placeholder="100, cien"
      data-testid="block-hueco-answer"
    />
  );
};

const BlockCard = ({ children, title, icon: Icon, onUp, onDown, onDelete, first, last }) => (
  <div className="border border-slate-200 rounded-lg bg-white shadow-sm" data-testid="lesson-block-card">
    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <Icon className="w-4 h-4 text-[#004E89]" /> {title}
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={onUp} disabled={first} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" title="Subir" data-testid="block-move-up"><ChevronUp className="w-4 h-4" /></button>
        <button type="button" onClick={onDown} disabled={last} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" title="Bajar" data-testid="block-move-down"><ChevronDown className="w-4 h-4" /></button>
        <button type="button" onClick={onDelete} className="p-1 rounded hover:bg-red-100 text-red-500" title="Eliminar" data-testid="block-delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
    <div className="p-3 space-y-3">{children}</div>
  </div>
);

const LessonBlockEditor = ({ blocks = [], onChange }) => {
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingVideoId, setUploadingVideoId] = useState(null);
  const [uploadPct, setUploadPct] = useState({});
  const [picker, setPicker] = useState({ open: false, type: 'image', blockId: null });

  const setPct = (id, v) => setUploadPct((p) => ({ ...p, [id]: v }));

  const update = (id, patch) => onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => onChange(blocks.filter((b) => b.id !== id));
  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    const copy = [...blocks];
    [copy[index], copy[target]] = [copy[target], copy[index]];
    onChange(copy);
  };

  const addBlock = (tipo) => {
    const base = { id: uid(), tipo };
    let nb = base;
    if (tipo === 'texto') nb = { ...base, contenido: '' };
    if (tipo === 'imagen') nb = { ...base, imagen_url: '', caption: '' };
    if (tipo === 'video') nb = { ...base, video_url: '', caption: '' };
    if (tipo === 'destacado') nb = { ...base, variante: 'objetivo', titulo: '', contenido: '' };
    if (tipo === 'pregunta') nb = {
      ...base, formato: 'opcion', pregunta: '', multiple: false, explicacion: '',
      opciones: [{ id: uid(), texto: '', correcta: true }, { id: uid(), texto: '', correcta: false }],
    };
    onChange([...blocks, nb]);
  };

  const handleImageUpload = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    setPct(id, 0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setPct(id, Math.round((e.loaded * 100) / e.total)); },
      });
      update(id, { imagen_url: res.data.url });
      toast.success('Imagen subida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploadingId(null);
    }
  };

  const handleVideoUpload = async (id, file) => {
    if (!file) return;
    setUploadingVideoId(id);
    setPct(id, 0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setPct(id, Math.round((e.loaded * 100) / e.total)); },
      });
      update(id, { video_url: res.data.url });
      toast.success('Vídeo subido');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir el vídeo');
    } finally {
      setUploadingVideoId(null);
    }
  };

  // ---- Opción múltiple helpers ----
  const updateOption = (blockId, optId, patch) => {
    const b = blocks.find((x) => x.id === blockId);
    update(blockId, { opciones: b.opciones.map((o) => (o.id === optId ? { ...o, ...patch } : o)) });
  };
  const setCorrect = (block, optId) => {
    if (block.multiple) {
      updateOption(block.id, optId, { correcta: !block.opciones.find((o) => o.id === optId).correcta });
    } else {
      update(block.id, { opciones: block.opciones.map((o) => ({ ...o, correcta: o.id === optId })) });
    }
  };
  const addOption = (block) => update(block.id, { opciones: [...block.opciones, { id: uid(), texto: '', correcta: false }] });
  const removeOption = (block, optId) => update(block.id, { opciones: block.opciones.filter((o) => o.id !== optId) });

  // ---- Rellenar huecos helpers ----
  const syncHuecos = (block, enunciado) => {
    const n = countBlanks(enunciado);
    const current = block.huecos || [];
    const huecos = Array.from({ length: n }, (_, i) => current[i] || { id: uid(), respuestas: [''] });
    update(block.id, { enunciado, huecos });
  };

  // ---- Relacionar columnas helpers ----
  const updatePar = (block, parId, patch) => update(block.id, { pares: block.pares.map((p) => (p.id === parId ? { ...p, ...patch } : p)) });
  const addPar = (block) => update(block.id, { pares: [...(block.pares || []), { id: uid(), izquierda: '', derecha: '' }] });
  const removePar = (block, parId) => update(block.id, { pares: block.pares.filter((p) => p.id !== parId) });

  const changeFormato = (block, formato) => {
    const patch = { formato };
    if (formato === 'opcion' && !block.opciones) patch.opciones = [{ id: uid(), texto: '', correcta: true }, { id: uid(), texto: '', correcta: false }];
    if (formato === 'huecos' && !block.huecos) { patch.enunciado = block.enunciado || ''; patch.huecos = []; }
    if (formato === 'relacionar' && !block.pares) patch.pares = [{ id: uid(), izquierda: '', derecha: '' }, { id: uid(), izquierda: '', derecha: '' }];
    update(block.id, patch);
  };

  const renderPregunta = (block) => {
    const formato = block.formato || 'opcion';
    return (
      <>
        <div>
          <Label className="text-xs">Formato de pregunta</Label>
          <Select value={formato} onValueChange={(v) => changeFormato(block, v)}>
            <SelectTrigger data-testid="question-format-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="opcion">Opción (test)</SelectItem>
              <SelectItem value="huecos">Rellenar huecos</SelectItem>
              <SelectItem value="relacionar">Relacionar columnas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formato === 'opcion' && (
          <>
            <div>
              <Label className="text-xs">Enunciado de la pregunta</Label>
              <Textarea value={block.pregunta || ''} onChange={(e) => update(block.id, { pregunta: e.target.value })} rows={2} placeholder="Escribe la pregunta..." data-testid="block-question-text" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={!!block.multiple} onChange={(e) => {
                const multiple = e.target.checked;
                let opciones = block.opciones;
                if (!multiple) {
                  const firstCorrect = opciones.find((o) => o.correcta);
                  opciones = opciones.map((o) => ({ ...o, correcta: firstCorrect ? o.id === firstCorrect.id : false }));
                }
                update(block.id, { multiple, opciones });
              }} />
              Permitir varias respuestas correctas
            </label>
            <div className="space-y-2">
              <Label className="text-xs">Opciones (marca la/las correctas)</Label>
              {block.opciones.map((o) => (
                <div key={o.id} className="flex items-center gap-2">
                  <button type="button" onClick={() => setCorrect(block, o.id)}
                    className={`w-6 h-6 flex-shrink-0 border flex items-center justify-center ${block.multiple ? 'rounded-md' : 'rounded-full'} ${o.correcta ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 text-transparent'}`}
                    title="Marcar como correcta" data-testid="block-option-correct"><Check className="w-4 h-4" /></button>
                  <Input value={o.texto} onChange={(e) => updateOption(block.id, o.id, { texto: e.target.value })} placeholder="Texto de la opción" className="flex-1" />
                  {block.opciones.length > 2 && (<button type="button" onClick={() => removeOption(block, o.id)} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>)}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addOption(block)} data-testid="block-add-option"><Plus className="w-4 h-4 mr-1" /> Añadir opción</Button>
            </div>
          </>
        )}

        {formato === 'huecos' && (
          <>
            <div>
              <Label className="text-xs">Texto con huecos (escribe <code>___</code> donde va cada hueco)</Label>
              <Textarea value={block.enunciado || ''} onChange={(e) => syncHuecos(block, e.target.value)} rows={3} placeholder="La RCP debe hacerse a ___ compresiones por minuto." data-testid="block-huecos-text" />
            </div>
            {(block.huecos || []).length === 0 && <p className="text-xs text-amber-600">Añade al menos un <code>___</code> en el texto.</p>}
            {(block.huecos || []).map((h, i) => (
              <div key={h.id}>
                <Label className="text-xs">Respuesta del hueco {i + 1} (separa alternativas con comas)</Label>
                <HuecoAnswerInput
                  value={h.respuestas}
                  onCommit={(respuestas) => update(block.id, { huecos: block.huecos.map((x) => (x.id === h.id ? { ...x, respuestas } : x)) })}
                />
              </div>
            ))}
          </>
        )}

        {formato === 'relacionar' && (
          <>
            <div>
              <Label className="text-xs">Enunciado (opcional)</Label>
              <Textarea value={block.pregunta || ''} onChange={(e) => update(block.id, { pregunta: e.target.value })} rows={1} placeholder="Relaciona cada concepto con su definición" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Parejas a relacionar</Label>
              {(block.pares || []).map((p, i) => (
                <div key={p.id} className="flex items-center gap-2">
                  <Input value={p.izquierda} onChange={(e) => updatePar(block, p.id, { izquierda: e.target.value })} placeholder={`Columna A #${i + 1}`} className="flex-1" data-testid="block-par-left" />
                  <span className="text-slate-400">↔</span>
                  <Input value={p.derecha} onChange={(e) => updatePar(block, p.id, { derecha: e.target.value })} placeholder={`Columna B #${i + 1}`} className="flex-1" data-testid="block-par-right" />
                  {(block.pares || []).length > 2 && (<button type="button" onClick={() => removePar(block, p.id)} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>)}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addPar(block)} data-testid="block-add-par"><Plus className="w-4 h-4 mr-1" /> Añadir pareja</Button>
            </div>
          </>
        )}

        <div>
          <Label className="text-xs">Explicación (se muestra tras responder, opcional)</Label>
          <Textarea value={block.explicacion || ''} onChange={(e) => update(block.id, { explicacion: e.target.value })} rows={2} placeholder="Explicación de la respuesta..." />
        </div>
      </>
    );
  };

  return (
    <div className="space-y-4" data-testid="lesson-block-editor">
      {blocks.length === 0 && (
        <div className="text-center py-6 text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
          Añade bloques para construir el tema (texto, imágenes, vídeos, recuadros y preguntas).
        </div>
      )}

      {blocks.map((block, index) => {
        const common = {
          first: index === 0, last: index === blocks.length - 1,
          onUp: () => move(index, -1), onDown: () => move(index, 1), onDelete: () => remove(block.id),
        };

        if (block.tipo === 'texto') {
          return (
            <BlockCard key={block.id} title="Texto" icon={Type} {...common}>
              <RichTextEditor value={block.contenido} onChange={(v) => update(block.id, { contenido: v })} placeholder="Escribe el texto..." />
            </BlockCard>
          );
        }

        if (block.tipo === 'imagen') {
          return (
            <BlockCard key={block.id} title="Imagen" icon={ImageIcon} {...common}>
              {block.imagen_url ? (
                <div className="space-y-2">
                  <img src={resolveImg(block.imagen_url)} alt="" className="max-h-56 rounded-md border border-slate-200 mx-auto" />
                  <Button type="button" variant="outline" size="sm" onClick={() => update(block.id, { imagen_url: '' })} data-testid="block-image-remove"><Trash2 className="w-4 h-4 mr-1" /> Quitar imagen</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                    <input type="file" accept="image/*" className="hidden" id={`img-upload-${block.id}`} data-testid="block-image-upload" onChange={(e) => handleImageUpload(block.id, e.target.files[0])} />
                    <label htmlFor={`img-upload-${block.id}`} className="cursor-pointer flex flex-col items-center gap-1 text-slate-500">
                      <Upload className="w-7 h-7 text-slate-400" />
                      <span className="text-sm">{uploadingId === block.id ? `Subiendo... ${uploadPct[block.id] || 0}%` : 'Subir imagen'}</span>
                      <span className="text-xs text-slate-400">JPG, PNG, WebP, GIF (máx. 5MB)</span>
                    </label>
                    {uploadingId === block.id && (
                      <Progress value={uploadPct[block.id] || 0} className="h-2 mt-3" data-testid="image-upload-progress" />
                    )}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setPicker({ open: true, type: 'image', blockId: block.id })} data-testid="block-image-library">
                    <FolderOpen className="w-4 h-4 mr-1" /> Elegir de la biblioteca
                  </Button>
                </div>
              )}
              <div>
                <Label className="text-xs">Pie de imagen (opcional)</Label>
                <Input value={block.caption || ''} onChange={(e) => update(block.id, { caption: e.target.value })} placeholder="Descripción..." data-testid="block-image-caption" />
              </div>
              <div>
                <Label className="text-xs">...o pega una URL externa</Label>
                <Input value={block.imagen_url?.startsWith('/api') ? '' : (block.imagen_url || '')} onChange={(e) => update(block.id, { imagen_url: e.target.value })} placeholder="https://..." disabled={block.imagen_url?.startsWith('/api')} />
              </div>
            </BlockCard>
          );
        }

        if (block.tipo === 'video') {
          const isUploaded = (block.video_url || '').startsWith('/api/uploads/video/');
          return (
            <BlockCard key={block.id} title="Vídeo" icon={Video} {...common}>
              {isUploaded ? (
                <div className="space-y-2">
                  <video src={resolveImg(block.video_url)} controls className="w-full max-h-56 rounded-md border border-slate-200 bg-black" />
                  <Button type="button" variant="outline" size="sm" onClick={() => update(block.id, { video_url: '' })} data-testid="block-video-remove">
                    <Trash2 className="w-4 h-4 mr-1" /> Quitar vídeo
                  </Button>
                </div>
              ) : (
                <>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                    <input type="file" accept="video/*" className="hidden" id={`video-upload-${block.id}`} data-testid="block-video-upload" onChange={(e) => handleVideoUpload(block.id, e.target.files[0])} />
                    <label htmlFor={`video-upload-${block.id}`} className="cursor-pointer flex flex-col items-center gap-1 text-slate-500">
                      <Upload className="w-7 h-7 text-slate-400" />
                      <span className="text-sm">{uploadingVideoId === block.id ? `Subiendo vídeo... ${uploadPct[block.id] || 0}%` : 'Subir vídeo desde el ordenador'}</span>
                      <span className="text-xs text-slate-400">MP4, WebM, OGG o MOV (máx. 300MB)</span>
                    </label>
                    {uploadingVideoId === block.id && (
                      <Progress value={uploadPct[block.id] || 0} className="h-2 mt-3" data-testid="video-upload-progress" />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">...o pega una URL o el código de inserción (YouTube/Vimeo)</Label>
                    <Textarea value={block.video_url || ''} onChange={(e) => update(block.id, { video_url: normalizeVideoInput(e.target.value) })} rows={2} placeholder='https://vimeo.com/...  o  pega el código de inserción <iframe ...>' data-testid="block-video-url" />
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPicker({ open: true, type: 'video', blockId: block.id })} data-testid="block-video-library">
                    <FolderOpen className="w-4 h-4 mr-1" /> Reutilizar un vídeo anterior
                  </Button>
                </>
              )}
              <div>
                <Label className="text-xs">Pie de vídeo (opcional)</Label>
                <Input value={block.caption || ''} onChange={(e) => update(block.id, { caption: e.target.value })} placeholder="Descripción..." />
              </div>
            </BlockCard>
          );
        }

        if (block.tipo === 'destacado') {
          return (
            <BlockCard key={block.id} title="Recuadro destacado" icon={Lightbulb} {...common}>
              <div>
                <Label className="text-xs">Tipo de recuadro</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(HIGHLIGHT_VARIANTS).map(([key, v]) => {
                    const VIcon = v.icon;
                    const active = block.variante === key;
                    return (
                      <button key={key} type="button" onClick={() => update(block.id, { variante: key })}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                        style={active ? { background: v.bg, borderColor: v.border, color: v.color } : { borderColor: '#e2e8f0', color: '#64748b' }}
                        data-testid={`block-variant-${key}`}><VIcon className="w-3.5 h-3.5" /> {v.label}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Título (opcional)</Label>
                <Input value={block.titulo || ''} onChange={(e) => update(block.id, { titulo: e.target.value })} placeholder={HIGHLIGHT_VARIANTS[block.variante]?.label} />
              </div>
              <div>
                <Label className="text-xs">Contenido</Label>
                <RichTextEditor value={block.contenido} onChange={(v) => update(block.id, { contenido: v })} placeholder="Escribe el contenido del recuadro..." />
              </div>
            </BlockCard>
          );
        }

        if (block.tipo === 'pregunta') {
          return (
            <BlockCard key={block.id} title="Pregunta" icon={HelpCircle} {...common}>
              {renderPregunta(block)}
            </BlockCard>
          );
        }
        return null;
      })}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
        <span className="text-xs text-slate-400 w-full">Añadir bloque:</span>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock('texto')} data-testid="add-block-texto"><Type className="w-4 h-4 mr-1" /> Texto</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock('imagen')} data-testid="add-block-imagen"><ImageIcon className="w-4 h-4 mr-1" /> Imagen</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock('video')} data-testid="add-block-video"><Video className="w-4 h-4 mr-1" /> Vídeo</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock('destacado')} data-testid="add-block-destacado"><Lightbulb className="w-4 h-4 mr-1" /> Recuadro</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock('pregunta')} data-testid="add-block-pregunta"><HelpCircle className="w-4 h-4 mr-1" /> Pregunta</Button>
      </div>

      <MediaPicker
        open={picker.open}
        type={picker.type}
        onClose={() => setPicker({ open: false, type: 'image', blockId: null })}
        onSelect={(url) => {
          if (picker.blockId) update(picker.blockId, picker.type === 'image' ? { imagen_url: url } : { video_url: url });
        }}
      />
    </div>
  );
};

export default LessonBlockEditor;
